import React, { useState, useEffect, useRef } from 'react';
import { finanzasAPI, paisesAPI, talleresAPI, pagosAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, TrendingUp, TrendingDown, DollarSign, Loader2, Upload, FileText, 
  Eye, X, ArrowRightLeft, BarChart3, Users, Wallet, Target, RefreshCw,
  CircleDollarSign, PiggyBank, Building2, GraduationCap, Download, FileSpreadsheet,
  CreditCard, CheckCircle, Clock, Trash2, Wand2, AlertTriangle, Search
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { formatApiError } from '../../utils/apiError';
import { formatNivelSuscripcion } from '../../utils/niveles';
import { useSearchParams, useNavigate } from 'react-router-dom';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];

const AdminFinanzas = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [pagosSuscripciones, setPagosSuscripciones] = useState([]);
  const [periodo, setPeriodo] = useState('todo');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCompraDivisa, setShowCompraDivisa] = useState(false);
  const [formType, setFormType] = useState('ingreso');
  const [saving, setSaving] = useState(false);
  const [comprobante, setComprobante] = useState(null);
  const [comprobantePreview, setComprobantePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [exporting, setExporting] = useState(false);

  // Reconciliación MercadoPago (solapa "Reconciliar MP")
  const [reconDias, setReconDias] = useState(30);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconResult, setReconResult] = useState(null); // { consultados, faltantes, dias_consultados }
  const [reconSeleccionados, setReconSeleccionados] = useState(new Set());
  const [reconAplicando, setReconAplicando] = useState(false);
  const [reconUltimoBatch, setReconUltimoBatch] = useState(null); // resultado del último aplicar
  // Filtros combinables solapa "Movimientos"
  const [filtroTipo, setFiltroTipo] = useState('todos');         // todos | ingreso | egreso
  const [filtroCategoria, setFiltroCategoria] = useState('todas'); // todas | <categoria>
  // Toggle de moneda en los gráficos del Dashboard
  const [chartCurrency, setChartCurrency] = useState('USD');      // USD | ARS
  // Toggle Vista compacta / detallada en solapa Movimientos (persiste en localStorage)
  const [vistaMovimientos, setVistaMovimientos] = useState(() => {
    try { return localStorage.getItem('finanzas_vista_movimientos') || 'detallada'; }
    catch { return 'detallada'; }
  });
  const cambiarVistaMovimientos = (v) => {
    setVistaMovimientos(v);
    try { localStorage.setItem('finanzas_vista_movimientos', v); } catch {}
  };
  const fileInputRef = useRef(null);
  const fileInputRefDivisa = useRef(null);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // Formatear fecha sin desfase de timezone
  // new Date('2026-03-30') interpreta en UTC, toLocaleDateString convierte a local → desfase de 1 día
  const formatFecha = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('es-AR', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };
  const formatFechaCorta = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('es-AR');
  };
  
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    tipo: 'ingreso',
    categoria: 'transferencia_bancaria',
    moneda: 'USD',
    monto: 0,
    notas: '',
    taller_id: '',
    es_nuevo_suscripto: false,
    nivel_destino: '',         // Para nueva_suscripcion desde status_report
    tipo_tramite_gestoria: '', // Para categoria='gestoria'
    forma_pago: 'mensual',     // 'mensual' | 'anual' — Fase D
  });

  // Sub-form: alta rápida de taller nuevo cuando no aparece en el dropdown
  const [tallerNuevoVisible, setTallerNuevoVisible] = useState(false);
  const [tallerNuevoData, setTallerNuevoData] = useState({
    razon_social: '', numero_anac: '', pais: 'Argentina', provincia: '',
    email: '', telefono: '', nivel_destino: 'estandar',
  });
  const [paisesActivos, setPaisesActivos] = useState([]);
  const [creandoTaller, setCreandoTaller] = useState(false);

  // Talleres para selector de suscripcion
  const [talleresSuscripcion, setTalleresSuscripcion] = useState([]);

  const [divisaForm, setDivisaForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    moneda_destino: 'USD',
    cantidad_destino: 0,
    moneda_origen: 'ARS',
    cantidad_origen: 0,
    plataforma: '',
    notas: ''
  });
  const [divisaComprobante, setDivisaComprobante] = useState(null);

  useEffect(() => {
    loadData();
  }, [periodo, filtroTipo, filtroCategoria]);

  // Cargar países activos al montar (para el sub-form de "Taller no listado")
  useEffect(() => {
    paisesAPI.getActivos()
      .then(r => setPaisesActivos(r.data.items || []))
      .catch(() => setPaisesActivos([]));
  }, []);

  // Detectar query params para deep-link desde Admin/Suscripciones:
  // /admin/finanzas?tab=pagos&open_suscripcion=1&taller_id=X&nivel=Y
  useEffect(() => {
    const tab = searchParams.get('tab');
    const openSusc = searchParams.get('open_suscripcion');
    const tallerId = searchParams.get('taller_id');
    const nivelDestino = searchParams.get('nivel');

    if (tab) setActiveTab(tab);

    if (openSusc === '1' && tallerId) {
      setFormType('ingreso');
      setFormData(prev => ({
        ...prev,
        categoria: 'suscripcion_mensual',
        taller_id: tallerId,
        nivel_destino: nivelDestino || prev.nivel_destino || '',
      }));
      // Cargar la lista de talleres de renovación para que el Select muestre el nombre del taller
      loadTalleresSuscripcion('renovacion');
      setShowForm(true);
      // Limpiar query params después de procesarlos para evitar re-disparos
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('open_suscripcion');
      newParams.delete('taller_id');
      newParams.delete('nivel');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = periodo === 'custom' && customDateFrom
        ? `${periodo}&fecha_desde=${customDateFrom}T00:00:00Z${customDateTo ? `&fecha_hasta=${customDateTo}T23:59:59Z` : ''}`
        : periodo;
      // Filtros para movimientos (Tipo + Categoría + rango de fechas cuando período=custom)
      const movParams = { limit: 50 };
      if (filtroTipo !== 'todos') movParams.tipo = filtroTipo;
      if (filtroCategoria !== 'todas') movParams.categoria = filtroCategoria;
      // Fix: el rango personalizado también debe filtrar la lista de movimientos,
      // no solo el resumen de balances. (Antes tocar "Aplicar" no hacía nada visible en la tabla.)
      if (periodo === 'custom' && customDateFrom) {
        movParams.fecha_desde = `${customDateFrom}T00:00:00Z`;
        if (customDateTo) {
          movParams.fecha_hasta = `${customDateTo}T23:59:59Z`;
        }
      }
      const [resumenRes, movRes, metricasRes, pagosRes] = await Promise.all([
        finanzasAPI.getResumen(params),
        finanzasAPI.getMovimientos(movParams),
        finanzasAPI.getMetricas(),
        finanzasAPI.getPagosSuscripciones({ limit: 100 })
      ]);
      setResumen(resumenRes.data);
      setMovimientos(movRes.data);
      setMetricas(metricasRes.data);
      setPagosSuscripciones(pagosRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (formData.monto <= 0) {
      toast.error('El monto debe ser mayor a 0');
      return;
    }
    setSaving(true);
    try {
      let comprobante_url = null;
      if (comprobante) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', comprobante);
        try {
          const uploadRes = await finanzasAPI.uploadComprobante(uploadFormData);
          comprobante_url = uploadRes.data.url;
        } catch (uploadError) {
          console.error('Error uploading comprobante:', uploadError);
        }
      }

      const data = {
        ...formData,
        tipo: formType,
        fecha: formData.fecha + 'T12:00:00',
        comprobante_url
      };

      // Si es nueva_suscripcion con sub-form "Taller no listado", crear el taller primero
      if (formType === 'ingreso' && formData.categoria === 'nueva_suscripcion' && tallerNuevoVisible) {
        if (!tallerNuevoData.razon_social.trim() || !tallerNuevoData.numero_anac.trim()) {
          toast.error('Completá Razón social y N° de matrícula del taller nuevo');
          setSaving(false);
          return;
        }
        if (!tallerNuevoData.nivel_destino) {
          toast.error('Elegí el nivel a asignar');
          setSaving(false);
          return;
        }
        setCreandoTaller(true);
        try {
          const nuevoTallerRes = await talleresAPI.create({
            razon_social: tallerNuevoData.razon_social.trim(),
            numero_anac: tallerNuevoData.numero_anac.trim(),
            ubicacion: {
              pais: tallerNuevoData.pais,
              provincia: tallerNuevoData.provincia,
            },
            email: tallerNuevoData.email || undefined,
            telefono: tallerNuevoData.telefono || undefined,
            nivel_suscripcion: tallerNuevoData.nivel_destino,
            estado_contractual: 'a_contactar',
          });
          data.taller_id = nuevoTallerRes.data.id;
          data.nivel_destino = tallerNuevoData.nivel_destino;
          toast.success(`Taller ${tallerNuevoData.razon_social} creado`);
        } catch (e) {
          toast.error(formatApiError(e, 'Error creando el taller nuevo'));
          setCreandoTaller(false);
          setSaving(false);
          return;
        }
        setCreandoTaller(false);
      }
      
      if (formType === 'ingreso' && ['suscripcion_mensual', 'nueva_suscripcion', 'pago_atrasado'].includes(formData.categoria) && data.taller_id) {
        // Pago de suscripción manual (renovación / nueva / atrasado) — usa endpoint especial
        // El backend deriva es_nuevo_suscripto de la categoría. `pago_atrasado` NO dispara emails.
        const res = await finanzasAPI.registrarPagoSuscripcion(data);
        const tallerNombre = res.data?.taller_nombre || 'Taller';
        const accion = formData.categoria === 'nueva_suscripcion'
          ? 'Nueva suscripción activada'
          : formData.categoria === 'pago_atrasado'
            ? 'Pago atrasado registrado (sin emails)'
            : 'Suscripción renovada';
        toast.success(`${accion} para ${tallerNombre}.`);
      } else if (formType === 'ingreso') {
        await finanzasAPI.registrarIngreso(data);
        toast.success('Ingreso registrado');
      } else {
        await finanzasAPI.registrarEgreso(data);
        toast.success('Egreso registrado');
      }
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al registrar movimiento'));
    } finally {
      setSaving(false);
    }
  };

  const handleCompraDivisa = async () => {
    if (divisaForm.cantidad_destino <= 0 || divisaForm.cantidad_origen <= 0) {
      toast.error('Los montos deben ser mayores a 0');
      return;
    }
    setSaving(true);
    try {
      let comprobante_url = null;
      if (divisaComprobante) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', divisaComprobante);
        try {
          const uploadRes = await finanzasAPI.uploadComprobante(uploadFormData);
          comprobante_url = uploadRes.data.url;
        } catch (uploadError) {
          console.error('Error uploading comprobante:', uploadError);
        }
      }

      const data = {
        ...divisaForm,
        fecha: divisaForm.fecha + 'T12:00:00',
        comprobante_url
      };
      
      const result = await finanzasAPI.registrarCompraDivisa(data);
      toast.success(result.data.message);
      setShowCompraDivisa(false);
      resetDivisaForm();
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al registrar compra de divisa'));
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      // Respetar filtros activos (fechas custom, tipo, categoría)
      const params = { periodo };
      if (periodo === 'custom' && customDateFrom) {
        params.fecha_desde = `${customDateFrom}T00:00:00Z`;
        if (customDateTo) {
          params.fecha_hasta = `${customDateTo}T23:59:59Z`;
        }
      }
      if (filtroTipo && filtroTipo !== 'todos') {
        params.tipo = filtroTipo;
      }
      if (filtroCategoria && filtroCategoria !== 'todas') {
        params.categoria = filtroCategoria;
      }

      const response = await finanzasAPI.exportarExcel(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Nombre de archivo con sufijos según filtros aplicados
      const sufijos = [];
      if (params.tipo) sufijos.push(params.tipo);
      if (params.categoria) sufijos.push(params.categoria);
      const sufijo = sufijos.length ? `_${sufijos.join('_')}` : '';
      a.download = `reporte_financiero_${periodo}${sufijo}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reporte Excel descargado');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar reporte');
    } finally {
      setExporting(false);
    }
  };

  // Cargar talleres según el tipo de operación (renovación o nueva suscripción)
  const loadTalleresSuscripcion = async (tipo = 'renovacion') => {
    try {
      const res = await finanzasAPI.getTalleresSuscripcion(tipo);
      setTalleresSuscripcion(res.data || []);
    } catch {
      setTalleresSuscripcion([]);
    }
  };

  const handleCategoriaChange = (v) => {
    setFormData({
      ...formData,
      categoria: v,
      taller_id: '',
      es_nuevo_suscripto: v === 'nueva_suscripcion',
      nivel_destino: '',
      tipo_tramite_gestoria: '',
    });
    setTallerNuevoVisible(false);
    if (v === 'suscripcion_mensual' || v === 'pago_atrasado') {
      loadTalleresSuscripcion('renovacion');
    } else if (v === 'nueva_suscripcion') {
      loadTalleresSuscripcion('nueva');
    }
  };

  const resetForm = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      tipo: 'ingreso',
      categoria: 'transferencia_bancaria',
      moneda: 'USD',
      monto: 0,
      notas: '',
      taller_id: '',
      es_nuevo_suscripto: false,
      nivel_destino: '',
      tipo_tramite_gestoria: '',
    });
    setTallerNuevoVisible(false);
    setTallerNuevoData({
      razon_social: '', numero_anac: '', pais: 'Argentina', provincia: '',
      email: '', telefono: '', nivel_destino: 'estandar',
    });
    setComprobante(null);
    setComprobantePreview(null);
  };

  const resetDivisaForm = () => {
    setDivisaForm({
      fecha: new Date().toISOString().split('T')[0],
      moneda_destino: 'USD',
      cantidad_destino: 0,
      moneda_origen: 'ARS',
      cantidad_origen: 0,
      plataforma: '',
      notas: ''
    });
    setDivisaComprobante(null);
  };

  const handleDeleteMovimiento = async (mov) => {
    const esAutomatico = mov.automatico;
    const montoStr = mov.moneda === 'ARS' ? '$' + mov.monto.toLocaleString('es-AR') : '$' + mov.monto.toFixed(2) + ' ' + mov.moneda;
    
    if (esAutomatico) {
      if (!window.confirm(
        `ATENCIÓN: Este es un movimiento AUTOMÁTICO generado por el sistema (ej: suscripción, capacitación).\n\n` +
        `${mov.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}: ${montoStr}\n` +
        `Categoría: ${getCategoriaLabel(mov.categoria)}\n\n` +
        `Eliminarlo afectará el balance general y no se puede deshacer.\n` +
        `¿Está SEGURO de que desea eliminarlo?`
      )) return;
      // Segunda confirmación para automáticos
      if (!window.confirm('CONFIRMAR: ¿Realmente desea eliminar este movimiento automático del sistema?')) return;
    } else {
      if (!window.confirm(`¿Eliminar este ${mov.tipo === 'ingreso' ? 'ingreso' : 'egreso'} de ${montoStr}?`)) return;
    }
    
    setDeleting(mov.id);
    try {
      await finanzasAPI.deleteMovimiento(mov.id);
      toast.success('Movimiento eliminado');
      loadData();
    } catch (error) {
      toast.error(formatApiError(error, 'Error al eliminar movimiento'));
    } finally {
      setDeleting(null);
    }
  };

  // Categorías
  const categoriasIngreso = [
    { value: 'suscripcion_mensual', label: 'Suscripción Mensual (renovación)' },
    { value: 'nueva_suscripcion', label: 'Nueva Suscripción (alta o status→premium)' },
    { value: 'pago_atrasado', label: 'Pago Atrasado (sin disparar emails)' },
    { value: 'gestoria', label: 'Gestoría' },
    { value: 'usuarios_extra', label: 'Usuarios Extra' },
    { value: 'documentos', label: 'Documentos/Manuales' },
    { value: 'capacitacion', label: 'Capacitación' },
    { value: 'capacidades_temporales', label: 'Capacidades Temporales' },
    { value: 'transferencia_bancaria', label: 'Transferencia Bancaria' },
    { value: 'compra_divisa', label: 'Compra de Divisa' },
    { value: 'otro', label: 'Otro' }
  ];
  const categoriasEgreso = [
    { value: 'sueldo', label: 'Sueldo' },
    { value: 'compra', label: 'Compra' },
    { value: 'servicio', label: 'Servicio' },
    { value: 'impuesto', label: 'Impuesto' },
    { value: 'comision_mp', label: 'Comisión MercadoPago' },
    { value: 'compra_divisa', label: 'Compra de Divisa' },
    { value: 'otro', label: 'Otro' }
  ];

  const getCategoriaLabel = (categoria) => {
    const todas = [...categoriasIngreso, ...categoriasEgreso];
    const found = todas.find(c => c.value === categoria);
    return found ? found.label : categoria;
  };

  // Badge de forma de pago — permite distinguir Débito Automático / Mensual / Anual
  // en Movimientos, así el admin ve rápidamente el origen del ingreso sin abrir el detalle.
  const getFormaPagoBadge = (mov) => {
    if (!mov || mov.tipo !== 'ingreso') return null;
    // Solo pagos de suscripción tienen forma_pago relevante para el badge
    if (mov.categoria !== 'suscripcion_mensual' && mov.categoria !== 'nueva_suscripcion') return null;
    const forma = (mov.forma_pago || '').toLowerCase();
    if (forma === 'debito_automatico') {
      return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px] px-1.5 py-0" title="Débito automático">DÉB.AUTO</Badge>;
    }
    if (forma === 'anual') {
      return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] px-1.5 py-0" title="Pago anual">ANUAL</Badge>;
    }
    // "mensual" o vacío (retrocompatibilidad con movimientos históricos)
    return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] px-1.5 py-0" title="Pago mensual">MENSUAL</Badge>;
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Solo se permiten archivos PDF o imágenes');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('El archivo no puede superar los 5MB');
        return;
      }
      setFile(file);
      if (file.type.startsWith('image/') && setPreview) {
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  // Calcular tipo de cambio automáticamente
  const tipoCambioCalculado = divisaForm.cantidad_destino > 0 
    ? (divisaForm.cantidad_origen / divisaForm.cantidad_destino).toFixed(2)
    : '0.00';

  // Preparar datos para gráfico de categorías (filtrados por moneda activa)
  const categoriasChartData = metricas?.ingresos_por_categoria 
    ? Object.entries(metricas.ingresos_por_categoria).map(([key, value]) => ({
        name: getCategoriaLabel(key),
        USD: value.USD || 0,
        ARS: value.ARS || 0,
        value: value[chartCurrency] || 0,
      })).filter(item => item.value > 0)
    : [];

  // Categorías unificadas para el filtro combinado (deduplicadas por value)
  const todasCategoriasFiltro = Array.from(
    new Map([...categoriasIngreso, ...categoriasEgreso].map(c => [c.value, c])).values()
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-7xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Wallet className="h-7 w-7 text-teal-600" />
              Centro Financiero
            </h1>
            <p className="text-slate-500 text-sm mt-1">Gestión integral de ingresos, egresos y divisas</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              className="border-green-200 text-green-700 hover:bg-green-50"
              onClick={() => { setFormType('ingreso'); setShowForm(true); }}
            >
              <TrendingUp className="mr-2 h-4 w-4" /> Ingreso
            </Button>
            <Button 
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => { setFormType('egreso'); setShowForm(true); }}
            >
              <TrendingDown className="mr-2 h-4 w-4" /> Egreso
            </Button>
            <Button 
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
              onClick={() => setShowCompraDivisa(true)}
            >
              <ArrowRightLeft className="mr-2 h-4 w-4" /> Compra de Dólares
            </Button>
            <Button 
              variant="outline"
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
              onClick={handleExportExcel}
              disabled={exporting}
            >
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              Exportar Excel
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <BarChart3 className="mr-2 h-4 w-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="movimientos" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <FileText className="mr-2 h-4 w-4" /> Movimientos
            </TabsTrigger>
            <TabsTrigger value="pagos" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <CreditCard className="mr-2 h-4 w-4" /> Pagos Recibidos
              {pagosSuscripciones.length > 0 && (
                <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">{pagosSuscripciones.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="reconciliar" className="data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700" data-testid="tab-reconciliar-mp">
              <Wand2 className="mr-2 h-4 w-4" /> Reconciliar MP
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
              </div>
            ) : (
              <>
                {/* KPIs principales */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Balance USD */}
                  <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white border-0 shadow-lg">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-sm font-medium">Balance USD</p>
                          <p className="text-3xl font-bold mt-1">
                            ${resumen?.totales?.balance_usd?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="bg-white/20 p-3 rounded-xl">
                          <CircleDollarSign className="h-8 w-8" />
                        </div>
                      </div>
                      <div className="mt-4 flex gap-4 text-sm">
                        <span className="text-emerald-100">
                          +${resumen?.totales?.ingresos_usd?.toFixed(2) || '0'} ing.
                        </span>
                        <span className="text-emerald-200">
                          -${resumen?.totales?.egresos_usd?.toFixed(2) || '0'} egr.
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Balance ARS */}
                  <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white border-0 shadow-lg">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm font-medium">Balance ARS</p>
                          <p className="text-3xl font-bold mt-1">
                            ${resumen?.totales?.balance_ars?.toLocaleString('es-AR', {maximumFractionDigits: 0}) || '0'}
                          </p>
                        </div>
                        <div className="bg-white/20 p-3 rounded-xl">
                          <PiggyBank className="h-8 w-8" />
                        </div>
                      </div>
                      <div className="mt-4 flex gap-4 text-sm">
                        <span className="text-blue-100">
                          +${resumen?.totales?.ingresos_ars?.toLocaleString('es-AR', {maximumFractionDigits: 0}) || '0'}
                        </span>
                        <span className="text-blue-200">
                          -${resumen?.totales?.egresos_ars?.toLocaleString('es-AR', {maximumFractionDigits: 0}) || '0'}
                        </span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Suscriptores */}
                  <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white border-0 shadow-lg">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm font-medium">Suscriptores</p>
                          <p className="text-3xl font-bold mt-1">
                            {metricas?.suscriptores?.total_pagos || 0}
                          </p>
                        </div>
                        <div className="bg-white/20 p-3 rounded-xl">
                          <Building2 className="h-8 w-8" />
                        </div>
                      </div>
                      <div className="mt-4 text-sm text-purple-100">
                        {metricas?.suscriptores?.total_talleres || 0} talleres totales - {metricas?.suscriptores?.total_usuarios || 0} usuarios
                      </div>
                    </CardContent>
                  </Card>

                  {/* Proyección Anual */}
                  <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-lg">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-amber-100 text-sm font-medium">Proyección Anual USD</p>
                          <p className="text-3xl font-bold mt-1">
                            ${metricas?.proyecciones?.anual_usd?.toFixed(0) || '0'}
                          </p>
                        </div>
                        <div className="bg-white/20 p-3 rounded-xl">
                          <Target className="h-8 w-8" />
                        </div>
                      </div>
                      <div className="mt-4 text-sm text-amber-100">
                        ~${metricas?.proyecciones?.promedio_mensual_usd?.toFixed(0) || '0'}/mes
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Gráficos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Toggle de moneda global para los gráficos */}
                  <div className="lg:col-span-2 flex justify-end">
                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
                      <button
                        onClick={() => setChartCurrency('USD')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          chartCurrency === 'USD'
                            ? 'bg-teal-500 text-white shadow'
                            : 'text-slate-600 hover:text-teal-600'
                        }`}
                        data-testid="chart-currency-usd-btn"
                      >
                        USD
                      </button>
                      <button
                        onClick={() => setChartCurrency('ARS')}
                        className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                          chartCurrency === 'ARS'
                            ? 'bg-blue-500 text-white shadow'
                            : 'text-slate-600 hover:text-blue-600'
                        }`}
                        data-testid="chart-currency-ars-btn"
                      >
                        ARS
                      </button>
                    </div>
                  </div>

                  {/* Gráfico de Ingresos por Mes */}
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-teal-500" />
                        Ingresos vs Egresos ({chartCurrency})
                      </CardTitle>
                      <CardDescription>Últimos 12 meses</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={metricas?.ingresos_por_mes || []}>
                            <defs>
                              <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorEgresos" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                            <XAxis 
                              dataKey="mes" 
                              tick={{ fontSize: 12 }}
                              tickFormatter={(value) => value?.slice(5) || value}
                            />
                            <YAxis tick={{ fontSize: 12 }} />
                            <Tooltip 
                              formatter={(value) => [
                                chartCurrency === 'ARS'
                                  ? `$${Number(value).toLocaleString('es-AR', {maximumFractionDigits: 0})}`
                                  : `$${Number(value).toFixed(2)}`,
                                ''
                              ]}
                              labelFormatter={(label) => `Mes: ${label}`}
                            />
                            <Area 
                              type="monotone" 
                              dataKey={chartCurrency === 'ARS' ? 'ingresos_ars' : 'ingresos_usd'}
                              name="Ingresos"
                              stroke="#10b981" 
                              fill="url(#colorIngresos)"
                              strokeWidth={2}
                            />
                            <Area 
                              type="monotone" 
                              dataKey={chartCurrency === 'ARS' ? 'egresos_ars' : 'egresos_usd'}
                              name="Egresos"
                              stroke="#ef4444" 
                              fill="url(#colorEgresos)"
                              strokeWidth={2}
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Gráfico de Categorías */}
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PieChart className="h-5 w-5 text-purple-500" />
                        Ingresos por Categoría ({chartCurrency})
                      </CardTitle>
                      <CardDescription>Distribución del período</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[300px]">
                        {categoriasChartData.length === 0 ? (
                          <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                            Sin ingresos en {chartCurrency} en el período
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={categoriasChartData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {categoriasChartData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => 
                                chartCurrency === 'ARS'
                                  ? `$${Number(value).toLocaleString('es-AR', {maximumFractionDigits: 0})}`
                                  : `$${Number(value).toFixed(2)}`
                              } />
                            </PieChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Suscriptores por Plan */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      Suscriptores por Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { key: 'status_report', label: 'Status Report', color: 'bg-slate-100 text-slate-700' },
                        { key: 'estandar', label: 'Standard', color: 'bg-teal-100 text-teal-700' },
                        { key: 'estandar_pro', label: 'Standard Pro', color: 'bg-blue-100 text-blue-700' },
                        { key: 'premium', label: 'Premium', color: 'bg-amber-100 text-amber-700' }
                      ].map(plan => (
                        <div key={plan.key} className={`${plan.color} rounded-xl p-4 text-center`}>
                          <p className="text-3xl font-bold">
                            {metricas?.suscriptores?.por_plan?.[plan.key] || 0}
                          </p>
                          <p className="text-sm mt-1 font-medium">{plan.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Compras de Divisas Recientes */}
                {metricas?.compras_divisas_recientes?.length > 0 && (
                  <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <ArrowRightLeft className="h-5 w-5 text-emerald-500" />
                        Compras de Divisas Recientes
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {metricas.compras_divisas_recientes.slice(0, 5).map((compra, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="bg-emerald-100 p-2 rounded-lg">
                                <ArrowRightLeft className="h-4 w-4 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium">
                                  {compra.cantidad_origen?.toLocaleString('es-AR')} {compra.moneda_origen} → {compra.cantidad_destino} {compra.moneda_destino}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {formatFechaCorta(compra.fecha)} 
                                  {compra.plataforma && ` • ${compra.plataforma}`}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                              TC: ${compra.tipo_cambio?.toFixed(2)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Movimientos Tab */}
          <TabsContent value="movimientos" className="space-y-6">
            {/* Periodo selector */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <Label className="mb-2 block text-sm">Periodo:</Label>
                    <Select value={periodo} onValueChange={(v) => { setPeriodo(v); if (v !== 'custom') { setCustomDateFrom(''); setCustomDateTo(''); } }}>
                      <SelectTrigger className="w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dia">Hoy</SelectItem>
                        <SelectItem value="semana">Esta semana</SelectItem>
                        <SelectItem value="mes">Este mes</SelectItem>
                        <SelectItem value="mes_pasado">Mes pasado</SelectItem>
                        <SelectItem value="anio">Este anio</SelectItem>
                        <SelectItem value="todo">Todo</SelectItem>
                        <SelectItem value="custom">Personalizado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {periodo === 'custom' && (
                    <>
                      <div>
                        <Label className="mb-2 block text-sm">Desde:</Label>
                        <Input
                          type="date"
                          value={customDateFrom}
                          onChange={(e) => setCustomDateFrom(e.target.value)}
                          className="w-40"
                        />
                      </div>
                      <div>
                        <Label className="mb-2 block text-sm">Hasta:</Label>
                        <Input
                          type="date"
                          value={customDateTo}
                          onChange={(e) => setCustomDateTo(e.target.value)}
                          className="w-40"
                        />
                      </div>
                      <Button 
                        variant="default" 
                        size="sm" 
                        onClick={loadData}
                        disabled={!customDateFrom}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        Aplicar
                      </Button>
                    </>
                  )}
                  {/* Filtro Tipo */}
                  <div>
                    <Label className="mb-2 block text-sm">Tipo:</Label>
                    <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                      <SelectTrigger className="w-40" data-testid="filtro-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todos">Todos</SelectItem>
                        <SelectItem value="ingreso">Ingresos</SelectItem>
                        <SelectItem value="egreso">Egresos</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Filtro Categoría */}
                  <div>
                    <Label className="mb-2 block text-sm">Categoría:</Label>
                    <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
                      <SelectTrigger className="w-56" data-testid="filtro-categoria-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="todas">Todas las categorías</SelectItem>
                        {todasCategoriasFiltro.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(filtroTipo !== 'todos' || filtroCategoria !== 'todas') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setFiltroTipo('todos'); setFiltroCategoria('todas'); }}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="limpiar-filtros-btn"
                    >
                      <X className="h-4 w-4 mr-1" /> Limpiar filtros
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={loadData}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Balances por moneda */}
            {resumen && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Card className="border-green-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-xl">🇺🇸</span> Dólares (USD)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Ingresos:</span>
                      <span className="text-green-600 font-medium">+${resumen.totales.ingresos_usd?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Egresos:</span>
                      <span className="text-red-600 font-medium">-${resumen.totales.egresos_usd?.toFixed(2) || '0.00'}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between">
                      <span className="font-medium">Balance:</span>
                      <span className={`text-xl font-bold ${(resumen.totales.balance_usd || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${resumen.totales.balance_usd?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-blue-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-xl">🇦🇷</span> Pesos Argentinos (ARS)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Ingresos:</span>
                      <span className="text-green-600 font-medium">+${resumen.totales.ingresos_ars?.toFixed(0) || '0'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Egresos:</span>
                      <span className="text-red-600 font-medium">-${resumen.totales.egresos_ars?.toFixed(0) || '0'}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between">
                      <span className="font-medium">Balance:</span>
                      <span className={`text-xl font-bold ${(resumen.totales.balance_ars || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${resumen.totales.balance_ars?.toFixed(0) || '0'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-yellow-200">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span className="text-xl">🇧🇷</span> Reales (BRL)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Ingresos:</span>
                      <span className="text-green-600 font-medium">+R${resumen.totales.ingresos_brl?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Egresos:</span>
                      <span className="text-red-600 font-medium">-R${resumen.totales.egresos_brl?.toFixed(2) || '0.00'}</span>
                    </div>
                    <hr />
                    <div className="flex justify-between">
                      <span className="font-medium">Balance:</span>
                      <span className={`text-xl font-bold ${(resumen.totales.balance_brl || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        R${resumen.totales.balance_brl?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Lista de movimientos */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle>Movimientos Recientes</CardTitle>
                  {/* Toggle Vista compacta / detallada */}
                  <div className="inline-flex rounded-md border bg-slate-50 p-0.5" data-testid="toggle-vista-movimientos">
                    <button
                      type="button"
                      onClick={() => cambiarVistaMovimientos('compacta')}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${vistaMovimientos === 'compacta' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid="vista-compacta-btn"
                      title="Vista compacta: más filas por pantalla"
                    >
                      Compacta
                    </button>
                    <button
                      type="button"
                      onClick={() => cambiarVistaMovimientos('detallada')}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${vistaMovimientos === 'detallada' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      data-testid="vista-detallada-btn"
                      title="Vista detallada: todos los datos visibles"
                    >
                      Detallada
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {movimientos.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No hay movimientos registrados</p>
                ) : vistaMovimientos === 'compacta' ? (
                  /* === VISTA COMPACTA: una línea por movimiento, sin botones === */
                  <div className="divide-y border rounded-lg overflow-hidden">
                    {movimientos.map(mov => (
                      <button
                        key={mov.id}
                        type="button"
                        onClick={() => setSelectedMovimiento(mov)}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors text-left"
                        data-testid={`mov-compacto-${mov.id}`}
                      >
                        {mov.tipo === 'ingreso' ? (
                          <TrendingUp className="h-4 w-4 text-green-500 shrink-0" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-500 shrink-0" />
                        )}
                        <span className="flex-1 min-w-0 text-sm font-medium truncate flex items-center gap-1.5">
                          <span className="truncate">{getCategoriaLabel(mov.categoria)}</span>
                          {getFormaPagoBadge(mov)}
                        </span>
                        <span className="text-[11px] text-gray-400 shrink-0 hidden sm:inline">
                          {formatFechaCorta(mov.fecha)}
                        </span>
                        <span className={`text-sm font-bold whitespace-nowrap shrink-0 ${mov.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                          {mov.tipo === 'ingreso' ? '+' : '-'}
                          {mov.moneda === 'ARS' ? `$${mov.monto.toLocaleString('es-AR', {maximumFractionDigits: 0})}` : `$${mov.monto.toFixed(2)}`}
                          <span className="text-[10px] text-gray-400 ml-1">{mov.moneda}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {movimientos.map(mov => (
                      <div key={mov.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {mov.tipo === 'ingreso' ? (
                            <div className="bg-green-100 p-2 rounded-lg shrink-0">
                              <TrendingUp className="h-5 w-5 text-green-500" />
                            </div>
                          ) : (
                            <div className="bg-red-100 p-2 rounded-lg shrink-0">
                              <TrendingDown className="h-5 w-5 text-red-500" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="font-medium break-words flex items-center gap-2 flex-wrap">
                              <span>{getCategoriaLabel(mov.categoria)}</span>
                              {getFormaPagoBadge(mov)}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                              <span>{formatFechaCorta(mov.fecha)}</span>
                              {mov.automatico && <Badge variant="outline" className="text-xs">Auto</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-2 pl-11 sm:pl-0">
                          <div className="text-left sm:text-right min-w-0">
                            <p className={`font-bold whitespace-nowrap ${mov.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                              {mov.tipo === 'ingreso' ? '+' : '-'}
                              {mov.moneda === 'ARS' ? `$${mov.monto.toLocaleString('es-AR', {maximumFractionDigits: 0})}` : `$${mov.monto.toFixed(2)}`} {mov.moneda}
                            </p>
                            {mov.notas && <p className="text-xs text-gray-500 max-w-[150px] truncate">{mov.notas}</p>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                            onClick={() => setSelectedMovimiento(mov)}
                            data-testid={`view-mov-${mov.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${mov.automatico 
                              ? 'text-slate-300 hover:text-amber-600 hover:bg-amber-50' 
                              : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                            onClick={() => handleDeleteMovimiento(mov)}
                            disabled={deleting === mov.id}
                            data-testid={`delete-mov-${mov.id}`}
                            title={mov.automatico ? 'Movimiento automático - eliminar con precaución' : 'Eliminar movimiento'}
                          >
                            {deleting === mov.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pagos Recibidos Tab */}
          <TabsContent value="pagos" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-emerald-600" />
                  Historial de Pagos de Suscripciones
                </CardTitle>
                <CardDescription>
                  Pagos de facturas mensuales y débito automático recibidos vía MercadoPago
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pagosSuscripciones.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No hay pagos de suscripciones registrados</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Los pagos aparecerán aquí cuando los talleres paguen sus facturas mensuales
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Resumen rápido — totales REALES, sin conversión BNA */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-testid="kpi-total-usd">
                        <p className="text-sm text-emerald-600 font-medium">Total Recibido en Dólares</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          ${pagosSuscripciones.reduce((acc, p) => {
                            // Solo cuenta USD real: pagos manuales en USD (transferencias/efectivo).
                            // MP siempre cobra en ARS — su `total_usd` es solo precio de referencia.
                            if (p.automatico) return acc;
                            return acc + (p.total_usd || 0);
                          }, 0).toLocaleString('en-US', {minimumFractionDigits: 2})}
                        </p>
                        <p className="text-[10px] text-emerald-500 mt-0.5">Solo transferencias/efectivo en USD</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" data-testid="kpi-total-ars">
                        <p className="text-sm text-blue-600 font-medium">Total Recibido en Pesos</p>
                        <p className="text-2xl font-bold text-blue-700">
                          ${pagosSuscripciones.reduce((acc, p) => acc + (p.monto_ars || 0), 0).toLocaleString('es-AR', {maximumFractionDigits: 0})}
                        </p>
                        <p className="text-[10px] text-blue-500 mt-0.5">MercadoPago + transferencias en ARS</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                        <p className="text-sm text-purple-600 font-medium">Pagos Recibidos</p>
                        <p className="text-2xl font-bold text-purple-700">{pagosSuscripciones.length}</p>
                      </div>
                    </div>

                    {/* Lista de pagos */}
                    <div className="border rounded-lg overflow-x-auto">
                      <table className="w-full min-w-[640px]">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taller</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto USD</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto ARS</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {pagosSuscripciones.map((pago) => (
                            <tr key={pago.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-900">{pago.taller_nombre || 'N/A'}</p>
                                <p className="text-xs text-gray-500">ANAC: {pago.taller_numero_anac || 'N/A'}</p>
                              </td>
                              <td className="px-4 py-3">
                                <Badge variant="outline" className={
                                  pago.nivel === 'premium' ? 'border-amber-500 text-amber-700 bg-amber-50' :
                                  pago.nivel === 'estandar_pro' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                                  'border-gray-500 text-gray-700 bg-gray-50'
                                }>
                                  {formatNivelSuscripcion(pago.nivel) || 'N/A'}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-emerald-600">
                                ${pago.total_usd?.toFixed(2) || '0.00'}
                              </td>
                              <td className="px-4 py-3 text-right text-gray-600">
                                ${pago.monto_ars?.toLocaleString('es-AR', {maximumFractionDigits: 0}) || '0'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500">
                                {pago.created_at ? new Date(pago.created_at).toLocaleDateString('es-AR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {pago.estado === 'completada' ? (
                                  <Badge className="bg-emerald-100 text-emerald-700">
                                    <CheckCircle className="h-3 w-3 mr-1" /> Completado
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary">
                                    <Clock className="h-3 w-3 mr-1" /> {pago.estado || 'Pendiente'}
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============================================================
              Solapa "Reconciliar MP" — Detecta pagos aprobados en MercadoPago
              que NO están en nuestra DB (webhooks perdidos, fallas de red)
              y permite reprocesarlos manualmente en lote.
              ============================================================ */}
          <TabsContent value="reconciliar" className="space-y-6" data-testid="tab-reconciliar-content">
            <Card className="border-l-4 border-l-orange-400">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="h-5 w-5 text-orange-500" />
                  Reconciliación con MercadoPago
                </CardTitle>
                <CardDescription>
                  Busca pagos <strong>aprobados en MercadoPago</strong> que no fueron registrados en nuestro sistema
                  (por ejemplo, si un webhook se perdió). Podés reprocesarlos manualmente.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-sm text-orange-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      Esta herramienta es <strong>segura y idempotente</strong> — si un pago ya está registrado, no se duplica.
                      Sólo pagos que se puedan mapear a un taller conocido podrán reconciliarse.
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label htmlFor="recon-dias" className="text-xs text-gray-500">Días hacia atrás</Label>
                    <Select value={String(reconDias)} onValueChange={(v) => setReconDias(parseInt(v))}>
                      <SelectTrigger id="recon-dias" className="w-32" data-testid="recon-dias-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">7 días</SelectItem>
                        <SelectItem value="15">15 días</SelectItem>
                        <SelectItem value="30">30 días</SelectItem>
                        <SelectItem value="60">60 días</SelectItem>
                        <SelectItem value="90">90 días</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={async () => {
                      setReconLoading(true);
                      setReconResult(null);
                      setReconSeleccionados(new Set());
                      setReconUltimoBatch(null);
                      try {
                        const res = await pagosAPI.reconciliarBuscar(reconDias);
                        setReconResult(res.data);
                        if ((res.data.faltantes || []).length === 0) {
                          toast.success(`No se encontraron pagos sin reconciliar en los últimos ${reconDias} días. ¡Todo en orden!`);
                        } else {
                          toast.warning(`Se encontraron ${res.data.faltantes.length} pagos sin reconciliar.`);
                        }
                      } catch (err) {
                        toast.error(formatApiError(err) || 'Error al consultar MercadoPago');
                      } finally {
                        setReconLoading(false);
                      }
                    }}
                    disabled={reconLoading}
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="recon-buscar-btn"
                  >
                    {reconLoading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Buscando en MP...</>
                    ) : (
                      <><Search className="h-4 w-4 mr-2" /> Buscar pagos no registrados</>
                    )}
                  </Button>
                </div>

                {/* Resultados del último aplicar */}
                {reconUltimoBatch && (
                  <div className={`border rounded-md p-3 text-sm ${
                    reconUltimoBatch.fallidos === 0
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}>
                    <div className="font-semibold mb-1 flex items-center gap-2">
                      {reconUltimoBatch.fallidos === 0 ? (
                        <><CheckCircle className="h-4 w-4" /> Reconciliación completada</>
                      ) : (
                        <><AlertTriangle className="h-4 w-4" /> Reconciliación parcial</>
                      )}
                    </div>
                    <div>
                      Total: {reconUltimoBatch.total} · Reconciliados: <strong>{reconUltimoBatch.reconciliados}</strong> · Fallidos: {reconUltimoBatch.fallidos}
                    </div>
                  </div>
                )}

                {/* Lista de faltantes */}
                {reconResult && reconResult.faltantes && reconResult.faltantes.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-sm text-gray-600">
                        Consultados: <strong>{reconResult.consultados}</strong> pagos en MP · 
                        <span className="text-orange-700 ml-1">Faltantes: {reconResult.faltantes.length}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const reconciliables = reconResult.faltantes.filter(f => f.puede_reconciliar).map(f => f.payment_id);
                            setReconSeleccionados(new Set(reconciliables));
                          }}
                          data-testid="recon-seleccionar-todos-btn"
                        >
                          Seleccionar reconciliables ({reconResult.faltantes.filter(f => f.puede_reconciliar).length})
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={reconSeleccionados.size === 0 || reconAplicando}
                          onClick={async () => {
                            if (reconSeleccionados.size === 0) return;
                            if (!window.confirm(`¿Reconciliar ${reconSeleccionados.size} pago(s)? Esta acción es idempotente pero se enviarán emails de confirmación y se actualizarán las suscripciones.`)) return;
                            setReconAplicando(true);
                            try {
                              const ids = Array.from(reconSeleccionados);
                              const res = await pagosAPI.reconciliarAplicar(ids);
                              setReconUltimoBatch(res.data);
                              if (res.data.reconciliados === res.data.total) {
                                toast.success(`${res.data.reconciliados} pago(s) reconciliado(s) correctamente`);
                              } else {
                                toast.warning(`Reconciliados: ${res.data.reconciliados} / ${res.data.total}. Ver detalles abajo.`);
                              }
                              // Refrescar la búsqueda
                              const nuevo = await pagosAPI.reconciliarBuscar(reconDias);
                              setReconResult(nuevo.data);
                              setReconSeleccionados(new Set());
                              // Refrescar movimientos y pagos recibidos también
                              loadData?.();
                            } catch (err) {
                              toast.error(formatApiError(err) || 'Error al aplicar reconciliación');
                            } finally {
                              setReconAplicando(false);
                            }
                          }}
                          data-testid="recon-aplicar-btn"
                        >
                          {reconAplicando ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Aplicando...</>
                          ) : (
                            <><Wand2 className="h-4 w-4 mr-1" /> Reconciliar {reconSeleccionados.size > 0 ? `(${reconSeleccionados.size})` : ''}</>
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse min-w-[720px]">
                        <thead>
                          <tr className="border-b bg-gray-50 text-left text-gray-600">
                            <th className="p-2 w-8"></th>
                            <th className="p-2">Fecha</th>
                            <th className="p-2">Payment ID</th>
                            <th className="p-2">Tipo</th>
                            <th className="p-2">Taller</th>
                            <th className="p-2">Monto</th>
                            <th className="p-2">Email pagador</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconResult.faltantes.map((f) => {
                            const seleccionada = reconSeleccionados.has(f.payment_id);
                            return (
                              <tr
                                key={f.payment_id}
                                className={`border-b hover:bg-gray-50 ${!f.puede_reconciliar ? 'opacity-60' : ''}`}
                                data-testid={`recon-row-${f.payment_id}`}
                              >
                                <td className="p-2">
                                  <input
                                    type="checkbox"
                                    checked={seleccionada}
                                    disabled={!f.puede_reconciliar}
                                    onChange={() => {
                                      const next = new Set(reconSeleccionados);
                                      if (next.has(f.payment_id)) next.delete(f.payment_id);
                                      else next.add(f.payment_id);
                                      setReconSeleccionados(next);
                                    }}
                                    data-testid={`recon-checkbox-${f.payment_id}`}
                                  />
                                </td>
                                <td className="p-2 text-xs">
                                  {f.fecha_aprobado ? new Date(f.fecha_aprobado).toLocaleString('es-AR', {
                                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                  }) : '—'}
                                </td>
                                <td className="p-2 font-mono text-xs">{f.payment_id}</td>
                                <td className="p-2">
                                  {f.tipo_ref === 'debito_automatico' ? (
                                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">Débito automático</Badge>
                                  ) : f.tipo_ref === 'suscripcion_mensual' ? (
                                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Suscripción</Badge>
                                  ) : f.tipo_ref === 'suscripcion_anual' ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Anual</Badge>
                                  ) : (
                                    <Badge variant="outline">{f.tipo_ref}</Badge>
                                  )}
                                </td>
                                <td className="p-2 text-xs">
                                  {f.taller ? (
                                    <div>
                                      <div className="font-medium">{f.taller.razon_social}</div>
                                      <div className="text-gray-500">{f.taller.numero_anac}</div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 italic">Sin mapeo</span>
                                  )}
                                </td>
                                <td className="p-2 whitespace-nowrap font-medium">
                                  {f.moneda} {Number(f.monto || 0).toLocaleString('es-AR')}
                                </td>
                                <td className="p-2 text-xs text-gray-600">{f.email_pagador || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="text-xs text-gray-500 italic">
                      * Los pagos "Sin mapeo" no tienen una suscripción de débito automático asociada en la DB — no pueden reconciliarse automáticamente.
                    </div>
                  </div>
                )}

                {reconResult && (!reconResult.faltantes || reconResult.faltantes.length === 0) && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-center text-emerald-800">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                    <div className="font-semibold">Todo en orden</div>
                    <div className="text-sm mt-1">
                      Consultamos {reconResult.consultados} pagos aprobados en MercadoPago de los últimos {reconResult.dias_consultados} días — todos están registrados en el sistema.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Dialog: Nuevo Ingreso/Egreso */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {formType === 'ingreso' ? (
                  <><TrendingUp className="h-5 w-5 text-green-500" /> Registrar Ingreso</>
                ) : (
                  <><TrendingDown className="h-5 w-5 text-red-500" /> Registrar Egreso</>
                )}
              </DialogTitle>
              <DialogDescription>
                Complete los datos del {formType === 'ingreso' ? 'ingreso' : 'egreso'} a registrar
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Categoria *</Label>
                  <Select 
                    value={formData.categoria}
                    onValueChange={handleCategoriaChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(formType === 'ingreso' ? categoriasIngreso : categoriasEgreso)
                        .filter(c => c.value !== 'compra_divisa')
                        .map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Selector de taller para suscripciones (mensual, nueva, atrasado) */}
              {formType === 'ingreso' && ['suscripcion_mensual', 'nueva_suscripcion', 'pago_atrasado'].includes(formData.categoria) && (
                <div className="space-y-3 p-3 bg-teal-50 border border-teal-200 rounded-lg" data-testid="form-suscripcion-block">
                  <div>
                    <Label className="text-teal-700">
                      Taller *
                      {formData.categoria === 'nueva_suscripcion' && (
                        <span className="ml-2 text-xs text-teal-500">(Status Report + sin suscripción)</span>
                      )}
                      {formData.categoria === 'suscripcion_mensual' && (
                        <span className="ml-2 text-xs text-teal-500">(solo activos)</span>
                      )}
                      {formData.categoria === 'pago_atrasado' && (
                        <span className="ml-2 text-xs text-amber-600">(no envía notificaciones)</span>
                      )}
                    </Label>
                    <Select 
                      value={formData.taller_id}
                      onValueChange={(v) => {
                        if (v === '__nuevo__') {
                          setTallerNuevoVisible(true);
                          setFormData({ ...formData, taller_id: '' });
                        } else {
                          setTallerNuevoVisible(false);
                          setFormData({ ...formData, taller_id: v });
                        }
                      }}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Seleccionar taller..." />
                      </SelectTrigger>
                      <SelectContent>
                        {talleresSuscripcion.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.razon_social} — {formatNivelSuscripcion(t.nivel_suscripcion)}
                            {t.estado_pago === 'suspendido' ? ' (SUSPENDIDO)' : ''}
                          </SelectItem>
                        ))}
                        {talleresSuscripcion.length === 0 && (
                          <SelectItem value="_none" disabled>
                            {formData.categoria === 'nueva_suscripcion' ? 'No hay talleres pendientes' : 'No hay talleres activos'}
                          </SelectItem>
                        )}
                        {formData.categoria === 'nueva_suscripcion' && (
                          <SelectItem value="__nuevo__" data-testid="opt-taller-nuevo">
                            + Taller no listado (cargar nuevo)
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Forma de pago: Mensual / Anual (Fase D) — solo para suscripcion_mensual y nueva_suscripcion */}
                  {(formData.categoria === 'suscripcion_mensual' || formData.categoria === 'nueva_suscripcion') && (
                    <div>
                      <Label className="text-teal-700">Forma de pago *</Label>
                      <Select
                        value={formData.forma_pago || 'mensual'}
                        onValueChange={(v) => setFormData({ ...formData, forma_pago: v })}
                      >
                        <SelectTrigger className="bg-white" data-testid="finanzas-forma-pago">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensual">📅 Mensual (31 días)</SelectItem>
                          <SelectItem value="anual">💎 Anual (365 días + 30 de gracia)</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.forma_pago === 'anual' && (
                        <p className="text-xs text-purple-700 mt-1 italic">
                          💎 Suscripción anual: 12 meses de acceso, sin cortes. Gracia ampliada (30 días).
                        </p>
                      )}
                    </div>
                  )}

                  {/* Nivel destino — obligatorio para nueva_suscripcion */}
                  {formData.categoria === 'nueva_suscripcion' && (formData.taller_id || tallerNuevoVisible) && (
                    <div>
                      <Label className="text-teal-700">Nivel a asignar *</Label>
                      <Select
                        value={tallerNuevoVisible ? tallerNuevoData.nivel_destino : formData.nivel_destino}
                        onValueChange={(v) => {
                          if (tallerNuevoVisible) setTallerNuevoData({...tallerNuevoData, nivel_destino: v});
                          else setFormData({...formData, nivel_destino: v});
                        }}
                      >
                        <SelectTrigger className="bg-white" data-testid="select-nivel-destino">
                          <SelectValue placeholder="Elegir nivel..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="estandar">Standard</SelectItem>
                          <SelectItem value="estandar_pro">Standard Pro</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Sub-form: alta rápida de taller nuevo */}
                  {tallerNuevoVisible && (
                    <div className="space-y-2 p-3 bg-white border border-teal-300 rounded">
                      <p className="text-xs font-semibold text-teal-700">Datos del taller nuevo</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Razón social *" value={tallerNuevoData.razon_social}
                          onChange={(e) => setTallerNuevoData({...tallerNuevoData, razon_social: e.target.value})}
                          data-testid="taller-nuevo-razon-social" />
                        <Input placeholder="N° de matrícula (ANAC) *" value={tallerNuevoData.numero_anac}
                          onChange={(e) => setTallerNuevoData({...tallerNuevoData, numero_anac: e.target.value})}
                          data-testid="taller-nuevo-anac" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={tallerNuevoData.pais} onValueChange={(v) => setTallerNuevoData({...tallerNuevoData, pais: v, provincia: ''})}>
                          <SelectTrigger><SelectValue placeholder="País" /></SelectTrigger>
                          <SelectContent>
                            {paisesActivos.map(p => (
                              <SelectItem key={p.id} value={p.nombre}>{p.bandera} {p.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={tallerNuevoData.provincia} onValueChange={(v) => setTallerNuevoData({...tallerNuevoData, provincia: v})}>
                          <SelectTrigger><SelectValue placeholder="Provincia / Estado" /></SelectTrigger>
                          <SelectContent>
                            {(paisesActivos.find(p => p.nombre === tallerNuevoData.pais)?.provincias || []).map(prov => (
                              <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="email" placeholder="Email contacto" value={tallerNuevoData.email}
                          onChange={(e) => setTallerNuevoData({...tallerNuevoData, email: e.target.value})}
                          data-testid="taller-nuevo-email" />
                        <Input placeholder="Teléfono" value={tallerNuevoData.telefono}
                          onChange={(e) => setTallerNuevoData({...tallerNuevoData, telefono: e.target.value})} />
                      </div>
                      <p className="text-xs text-amber-600">
                        ⚠️ Al guardar el formulario, primero se creará este taller (con el nivel seleccionado arriba) y luego se registra el pago.
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-teal-600">
                    {formData.categoria === 'pago_atrasado'
                      ? 'Se registra el pago en el historial. NO se enviarán emails ni notificaciones.'
                      : 'Al registrar, se renueva automáticamente el acceso del taller por 30 días.'}
                  </p>
                </div>
              )}

              {/* Sub-campo "Tipo de trámite" para categoría Gestoría */}
              {formType === 'ingreso' && formData.categoria === 'gestoria' && (
                <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2">
                  <Label className="text-violet-700">Tipo de trámite</Label>
                  <Input
                    placeholder="Ej: Habilitación inicial, Renovación, Modificación de capacidades, Otro..."
                    value={formData.tipo_tramite_gestoria}
                    onChange={(e) => setFormData({...formData, tipo_tramite_gestoria: e.target.value})}
                    data-testid="input-tipo-tramite-gestoria"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Monto *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <Label>Moneda *</Label>
                  <Select 
                    value={formData.moneda}
                    onValueChange={(v) => setFormData({ ...formData, moneda: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD (Dólares)</SelectItem>
                      <SelectItem value="ARS">ARS (Pesos Arg.)</SelectItem>
                      <SelectItem value="BRL">BRL (Reales)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Descripción opcional..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Comprobante (PDF o imagen)</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleFileChange(e, setComprobante, setComprobantePreview)}
                  className="hidden"
                />
                {!comprobante ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Adjuntar comprobante
                  </Button>
                ) : (
                  <div className="mt-2 p-3 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-blue-500" />
                        <span className="text-sm truncate max-w-[200px]">{comprobante.name}</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setComprobante(null); setComprobantePreview(null); }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Button onClick={handleSubmit} disabled={saving} className="w-full">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Registrar {formType === 'ingreso' ? 'Ingreso' : 'Egreso'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Compra de Divisas */}
        <Dialog open={showCompraDivisa} onOpenChange={setShowCompraDivisa}>

          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-teal-500" />
                Compra de Divisas
              </DialogTitle>
              <DialogDescription>
                Registra una compra de dólares u otra moneda. Se creará automáticamente un egreso en pesos y un ingreso en la moneda destino.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Fecha de la operación</Label>
                <Input
                  type="date"
                  value={divisaForm.fecha}
                  onChange={(e) => setDivisaForm({ ...divisaForm, fecha: e.target.value })}
                />
              </div>

              {/* Moneda que gasto */}
              <div className="p-4 bg-red-50 rounded-lg space-y-3">
                <Label className="text-red-700 font-medium">Gasto (Egreso)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Cantidad</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={divisaForm.cantidad_origen}
                      onChange={(e) => setDivisaForm({ ...divisaForm, cantidad_origen: parseFloat(e.target.value) || 0 })}
                      placeholder="Ej: 100000"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Moneda</Label>
                    <Select 
                      value={divisaForm.moneda_origen}
                      onValueChange={(v) => setDivisaForm({ ...divisaForm, moneda_origen: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ARS">ARS (Pesos)</SelectItem>
                        <SelectItem value="USD">USD (Dólares)</SelectItem>
                        <SelectItem value="BRL">BRL (Reales)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Flecha */}
              <div className="flex justify-center">
                <div className="bg-teal-100 p-2 rounded-full">
                  <TrendingDown className="h-5 w-5 text-teal-600 rotate-180" />
                </div>
              </div>

              {/* Moneda que recibo */}
              <div className="p-4 bg-green-50 rounded-lg space-y-3">
                <Label className="text-green-700 font-medium">Recibo (Ingreso)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Cantidad</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={divisaForm.cantidad_destino}
                      onChange={(e) => setDivisaForm({ ...divisaForm, cantidad_destino: parseFloat(e.target.value) || 0 })}
                      placeholder="Ej: 100"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Moneda</Label>
                    <Select 
                      value={divisaForm.moneda_destino}
                      onValueChange={(v) => setDivisaForm({ ...divisaForm, moneda_destino: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD (Dólares)</SelectItem>
                        <SelectItem value="USDT">USDT (Tether)</SelectItem>
                        <SelectItem value="BTC">BTC (Bitcoin)</SelectItem>
                        <SelectItem value="ETH">ETH (Ethereum)</SelectItem>
                        <SelectItem value="EUR">EUR (Euros)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Tipo de cambio calculado */}
              <div className="p-3 bg-slate-100 rounded-lg text-center">
                <p className="text-sm text-slate-600">Tipo de cambio:</p>
                <p className="text-2xl font-bold text-slate-800">
                  1 {divisaForm.moneda_destino} = ${tipoCambioCalculado} {divisaForm.moneda_origen}
                </p>
              </div>

              <div>
                <Label>Plataforma / Lugar</Label>
                <Input
                  value={divisaForm.plataforma}
                  onChange={(e) => setDivisaForm({ ...divisaForm, plataforma: e.target.value })}
                  placeholder="Ej: Banco Nación, Binance, Casa de cambio..."
                />
              </div>

              <div>
                <Label>Notas</Label>
                <Textarea
                  value={divisaForm.notas}
                  onChange={(e) => setDivisaForm({ ...divisaForm, notas: e.target.value })}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>

              <div>
                <Label>Comprobante</Label>
                <input
                  ref={fileInputRefDivisa}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleFileChange(e, setDivisaComprobante, null)}
                  className="hidden"
                />
                {!divisaComprobante ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => fileInputRefDivisa.current?.click()}
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    Adjuntar comprobante
                  </Button>
                ) : (
                  <div className="mt-2 p-3 border rounded-lg bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="text-sm truncate max-w-[200px]">{divisaComprobante.name}</span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setDivisaComprobante(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              <Button 
                onClick={handleCompraDivisa} 
                disabled={saving} 
                className="w-full bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700"
              >
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightLeft className="mr-2 h-4 w-4" />}
                Registrar Compra
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog: Detalle del Movimiento */}
        <Dialog open={!!selectedMovimiento} onOpenChange={() => setSelectedMovimiento(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedMovimiento?.tipo === 'ingreso' ? (
                  <><TrendingUp className="h-5 w-5 text-green-500" /> Detalle del Ingreso</>
                ) : (
                  <><TrendingDown className="h-5 w-5 text-red-500" /> Detalle del Egreso</>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedMovimiento && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Fecha</p>
                    <p className="font-medium">{formatFecha(selectedMovimiento.fecha)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Categoría</p>
                    <p className="font-medium">{getCategoriaLabel(selectedMovimiento.categoria)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Monto</p>
                    <p className={`text-xl font-bold ${selectedMovimiento.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedMovimiento.tipo === 'ingreso' ? '+' : '-'}
                      {selectedMovimiento.moneda === 'ARS' 
                        ? `$${selectedMovimiento.monto.toLocaleString('es-AR', {maximumFractionDigits: 0})}` 
                        : `$${selectedMovimiento.monto.toFixed(2)}`} {selectedMovimiento.moneda}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Tipo</p>
                    <Badge className={selectedMovimiento.tipo === 'ingreso' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {selectedMovimiento.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                    </Badge>
                    {selectedMovimiento.automatico && <Badge variant="outline" className="ml-2">Automático</Badge>}
                  </div>
                </div>
                {selectedMovimiento.notas && (
                  <div>
                    <p className="text-sm text-gray-500">Observaciones</p>
                    <p className="mt-1 p-3 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap">{selectedMovimiento.notas}</p>
                  </div>
                )}
                {selectedMovimiento.comprobante_url && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Comprobante</p>
                    <a 
                      href={`${selectedMovimiento.comprobante_url}${selectedMovimiento.comprobante_url.includes('?') ? '&' : '?'}token=${localStorage.getItem('token') || ''}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:underline text-sm"
                    >
                      <FileText className="h-4 w-4" /> Ver comprobante
                    </a>
                  </div>
                )}
                {selectedMovimiento.created_at && (
                  <div>
                    <p className="text-sm text-gray-500">Registrado</p>
                    <p className="text-xs text-gray-400">{new Date(selectedMovimiento.created_at).toLocaleString('es-AR')}</p>
                  </div>
                )}
                <div className="flex justify-end pt-2">
                  <Button
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => {
                      handleDeleteMovimiento(selectedMovimiento);
                      setSelectedMovimiento(null);
                    }}
                    data-testid="delete-from-detail"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Eliminar movimiento
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminFinanzas;
